import React, { useEffect, useState, useMemo } from "react";
import { fetchTables, fetchTableDetails } from "../services/schemaService";

export default function SchemaExplorer() {
  const [tables, setTables] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("");
  const [details, setDetails] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = await fetchTables();
        setTables(t);
        if (t?.[0]?.TABLE_NAME) setActive(t[0].TABLE_NAME);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!active) { setDetails(null); return; }
    setLoading(true);
    (async () => {
      try {
        const d = await fetchTableDetails(active);
        setDetails(d);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [active]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tables;
    return tables.filter(t => String(t.TABLE_NAME).toLowerCase().includes(s));
  }, [q, tables]);

  const copy = (text) => navigator.clipboard?.writeText(text).catch(()=>{});

  return (
    <div className="container py-3 py-md-4">
      <h1 className="h4 mb-3">Structure de la base</h1>
      {err && <div className="alert alert-danger">{err}</div>}

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                <input className="form-control" placeholder="Rechercher une table…" value={q} onChange={e=>setQ(e.target.value)} />
              </div>
              <div className="list-group" style={{ maxHeight: 520, overflow: 'auto' }}>
                {filtered.map(t => (
                  <button key={t.TABLE_NAME}
                          className={`list-group-item list-group-item-action ${active===t.TABLE_NAME?'active':''}`}
                          onClick={()=>setActive(t.TABLE_NAME)}>
                    <div className="d-flex justify-content-between">
                      <span>{t.TABLE_NAME}</span>
                      {t.TABLE_ROWS!=null && <span className="badge bg-secondary">{t.TABLE_ROWS}</span>}
                    </div>
                    {t.TABLE_COMMENT && <small className="text-muted d-block">{t.TABLE_COMMENT}</small>}
                  </button>
                ))}
                {filtered.length===0 && <div className="text-muted small p-2">Aucune table</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="h5 m-0">{active || 'Sélectionnez une table'}</h2>
                {active && (
                  <button className="btn btn-sm btn-outline-secondary"
                          onClick={()=>copy(`SELECT * FROM \`${active}\` LIMIT 50;`)}>
                    Copier SELECT
                  </button>
                )}
              </div>

              {loading && <div className="text-muted">Chargement…</div>}

              {!loading && details && (
                <>
                  <h6 className="mt-2">Colonnes</h6>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Colonne</th>
                          <th>Type</th>
                          <th>Null</th>
                          <th>Défaut</th>
                          <th>PK</th>
                          <th>Extra</th>
                          <th>Commentaire</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.columns.map((c) => {
                          const isPk = details.primaryKey?.includes(c.COLUMN_NAME);
                          return (
                            <tr key={c.COLUMN_NAME} className={isPk? 'table-light':''}>
                              <td>{c.ORDINAL_POSITION}</td>
                              <td><code>{c.COLUMN_NAME}</code></td>
                              <td>{c.COLUMN_TYPE}</td>
                              <td>{c.IS_NULLABLE}</td>
                              <td><code>{c.COLUMN_DEFAULT ?? ''}</code></td>
                              <td>{isPk ? '✔' : ''}</td>
                              <td>{c.EXTRA}</td>
                              <td className="small text-muted">{c.COLUMN_COMMENT}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {details.foreignKeys?.length > 0 && (
                    <>
                      <h6 className="mt-3">Clés étrangères</h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Colonne</th>
                              <th>Référence</th>
                              <th>Update</th>
                              <th>Delete</th>
                              <th>Nom</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.foreignKeys.map((f, i) => (
                              <tr key={`${f.CONSTRAINT_NAME}-${i}`}>
                                <td><code>{f.COLUMN_NAME}</code></td>
                                <td>{f.REFERENCED_TABLE_NAME}(<code>{f.REFERENCED_COLUMN_NAME}</code>)</td>
                                <td>{f.UPDATE_RULE}</td>
                                <td>{f.DELETE_RULE}</td>
                                <td className="small text-muted">{f.CONSTRAINT_NAME}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {details.indexes?.length > 0 && (
                    <>
                      <h6 className="mt-3">Index</h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Index</th>
                              <th>Colonne</th>
                              <th>Unique</th>
                              <th>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.indexes.map((r, i) => (
                              <tr key={`${r.INDEX_NAME}-${r.SEQ_IN_INDEX}-${i}`}>
                                <td>{r.INDEX_NAME}</td>
                                <td><code>{r.COLUMN_NAME}</code></td>
                                <td>{r.NON_UNIQUE === 0 ? 'Oui' : 'Non'}</td>
                                <td>{r.INDEX_TYPE}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {details.createSql && (
                    <>
                      <h6 className="mt-3">DDL</h6>
                      <pre className="small bg-light p-2 rounded" style={{whiteSpace:'pre-wrap'}}>{details.createSql}</pre>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
